import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { ApiResponse } from '../../api-response';
import { BaseApiService } from '../../../core/base-api.service';
import { TenantSetting } from '../models/tenant-setting.model';

/**
 * Tenant-visible settings (base path /api/v1/tenant-setting). The tenant is
 * derived from the JWT on the backend — there is no tenant parameter.
 * Reading needs the viewer role; writing/resetting needs tenant-admin.
 */
@Injectable({ providedIn: 'root' })
export class TenantSettingsService extends BaseApiService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${this.coreApiUrl}/api/v1/tenant-setting`;

  /** All TENANT_ONLY and USER_OVERRIDABLE settings, sorted by category then key. */
  getAll(): Observable<ApiResponse<TenantSetting[]>> {
    return this.http.get<ApiResponse<TenantSetting[]>>(this.baseUrl);
  }

  /** Stores a tenant override. The body is the raw JSON value ("sl-SI", 60, {...}). */
  update(key: string, value: unknown): Observable<ApiResponse<null>> {
    // Encode explicitly: HttpClient sends a plain string body verbatim as
    // text/plain, but the API expects a JSON document ('sl-SI' must go out
    // as "sl-SI" with application/json).
    return this.http.put<ApiResponse<null>>(
      `${this.baseUrl}/${encodeURIComponent(key)}`,
      JSON.stringify(value),
      { headers: { 'Content-Type': 'application/json' } },
    );
  }

  /** Removes the override so the setting falls back to the platform default. Idempotent. */
  reset(key: string): Observable<ApiResponse<null>> {
    return this.http.delete<ApiResponse<null>>(`${this.baseUrl}/${encodeURIComponent(key)}`);
  }
}
